import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppleIcon, SmartphoneIcon } from 'lucide-react';
import { Button } from './ui/Button';
import { appLinks } from '../data/appTour';
interface JoinPlatformButtonProps {
  /** Classes applied to the trigger button so it can match its surrounding hero/CTA. */
  className?: string;
  label?: string;
  /** Base button style; trigger classes in `className` refine it. */
  variant?: 'primary' | 'outline';
}
const MENU_WIDTH = 256;
export function JoinPlatformButton({
  className = '',
  label = 'Join the Platform',
  variant = 'primary'
}: JoinPlatformButtonProps) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0
  });
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // The trigger sits inside sections that clip their overflow, so the menu is
  // rendered in a portal and positioned against the button's viewport rect.
  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const maxLeft = window.innerWidth - MENU_WIDTH - 16;
    const centered = rect.left + rect.width / 2 - MENU_WIDTH / 2;
    setPosition({
      top: rect.bottom + 12,
      left: Math.max(16, Math.min(centered, maxLeft))
    });
  }, []);
  useEffect(() => {
    if (!open) return;
    reposition();
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open, reposition]);
  const optionStyles = 'flex items-center gap-3 px-5 py-3.5 text-sm font-medium text-charcoal hover:bg-purple-50 hover:text-purple-800 transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-600';
  return <div ref={triggerRef} className="w-full sm:w-auto" data-id="element-225" data-mp="18hptr3y9f1uj">
      <Button variant={variant} size="lg" onClick={() => setOpen(value => !value)} aria-expanded={open} aria-haspopup="true" aria-controls="get-the-app-options" className={className} data-id="element-226" data-mp="1d9pqyomy3gnf">
        {label}
      </Button>

      {open && createPortal(<div ref={menuRef} id="get-the-app-options" style={{
      top: position.top,
      left: position.left,
      width: MENU_WIDTH
    }} className="fixed z-[100] overflow-hidden rounded-2xl border border-purple-200 bg-white shadow-xl" data-id="element-227" data-mp="d94cce65x14r">
            <p className="px-5 pt-4 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-purple-700" data-id="element-228" data-mp="3vhv54hew75bz">
              Download the app
            </p>
            <a href={appLinks.apple} target="_blank" rel="noopener noreferrer" className={`${optionStyles} border-t border-purple-100`} data-id="element-229" data-mp="3loxt8u4eoemc">
              <AppleIcon aria-hidden="true" className="w-4 h-4 text-purple-700" data-id="element-230" data-mp="3bt48ingj9eth" />
              Download for iPhone
            </a>
            <a href={appLinks.android} target="_blank" rel="noopener noreferrer" className={`${optionStyles} border-t border-purple-100`} data-id="element-231" data-mp="32qlacgnozqdk">
              <SmartphoneIcon aria-hidden="true" className="w-4 h-4 text-purple-700" data-id="element-232" data-mp="1aptp1qj8ra0u" />
              Download for Android
            </a>
          </div>, document.body)}
    </div>;
}